/**
 * The Veil transmuting a dropped item with its essence orb (#259).
 *
 * The item and the orb swirl about each other, slowly at first and faster as
 * they close in, lifting a little off the ground; they meet in a flash, and
 * the new item drops back to the ground where they were and twinkles for a
 * while (or until it is picked up).
 *
 * The two bodies themselves are drawn by their own code: the item by its
 * `DroppedItemModel`, the orb by the `EssenceOrbSystem` (carried, through its
 * `held` state). This system says where each one is at every moment
 * (`resolveVeilTransmutationPose`) and draws everything else: a faint twirl
 * on the floor under the pair, wisps trailing each of them, the flash, and
 * the glints after. Every sprite is one of the shared Kenney particle sprites
 * (`particle-sprites.ts`), drawn through `SpriteEffectSystem` where it is a
 * one-off.
 *
 * Its glints and wisps are only visuals, so they may use `Math.random`.
 */

import { Color3, MeshBuilder, Vector3, type Mesh, type Scene, type StandardMaterial } from '@babylonjs/core';
import { DROPPED_ITEM_HOVER_HEIGHT_TILES } from '../dropped-item-model';
import { ESSENCE_ORB_HOVER_HEIGHT_TILES } from './essence-orb-system';
import { createSpriteMaterial, SPARK_LOOK, type ParticleSprite, type ParticleSpriteLook } from './particle-sprites';
import { SpriteEffectSystem, type SpriteQuadHandle, type SpriteStreamHandle } from './sprite-effect-system';
import {
    clampProgress,
    veilSettlePose,
    veilSwirlPose,
    veilSwirlAngle,
    veilTwinkleStrength,
    VEIL_SWIRL_LIFT_TILES,
    type VeilOffset,
} from './veil-transmutation-motion';

/** A point in the scene, matching `ScenePosition` without importing it. */
interface Point3 {
    readonly x: number;
    readonly y: number;
    readonly z: number;
}

/** One dropped item being (or just) transmuted, as the render worker knows it. */
export interface VeilTransmutationRenderState {
    /** The dropped item's id. */
    readonly id: string;
    /** Scene-space floor position of the item: where they meet and the new item lies. */
    readonly floorPosition: Point3;
    /** Scene-space floor position the orb rested at before the swirl. */
    readonly orbFloorPosition: Point3;
    /** The orb's essence colors, dominant first: its wisps, the twirl and the flash take the dominant one. */
    readonly orbColorHexes: readonly string[];
    /** **Linear** 0..1 progress through the swirl while they are being joined; null once it is done. */
    readonly swirlProgress: number | null;
    /** Seconds since it was done (the flash); null while they are being joined. */
    readonly secondsSinceDone: number | null;
    /** How long the new item twinkles after the flash, in seconds. */
    readonly twinkleSeconds: number;
}

/** Where its two bodies are drawn at one moment. */
export interface VeilTransmutationPose {
    /** The item model's offset from where it would rest (its own hover over its floor position), in scene units. */
    readonly itemOffset: VeilOffset;
    /** The item model's size, as a share of its own. */
    readonly itemScale: number;
    /** Where the orb's flame is drawn (scene space) and its size; null once it is spent in the flash. */
    readonly orb: { readonly center: Point3; readonly scale: number } | null;
}

/** The twirl on the floor under the swirling pair. */
const TWIRL_SPRITE: ParticleSprite = { family: 'twirl', index: 2 };
const TWIRL_DIAMETER_TILES = 1.3;
const TWIRL_ALPHA = 0.55;
/** It turns this many times as fast as the pair, so it reads as being dragged round by them. */
const TWIRL_SPIN_SHARE = 0.6;
/** Wisps trailing each body. */
const WISP_LOOK: ParticleSpriteLook = { sprites: [{ family: 'light', index: 1 }, { family: 'light', index: 2 }], spin: 1.2 };
const WISP_RATE = 28;
/** The item's wisps: a pale gold, so the two strands read apart. */
const ITEM_WISP_COLOR = Color3.FromHexString('#ffe7a3');
/** The flash, the spark burst and the floor ring as they meet. */
const FLASH_SPRITE: ParticleSprite = { family: 'flare', index: 1 };
const FLASH_STAR_SPRITE: ParticleSprite = { family: 'star', index: 6 };
const FLASH_RING_SPRITE: ParticleSprite = { family: 'circle', index: 2 };
const FLASH_SECONDS = 0.45;
/** A flash first heard of later than this after it happened (a slow frame, a late message) is skipped. */
const FLASH_LATEST_SECONDS = 0.5;
/** Its color: the orb's, washed toward white. */
const FLASH_WHITE_SHARE = 0.55;
/** The glints on the new item. */
const GLINT_SPRITES: readonly ParticleSprite[] = [{ family: 'star', index: 1 }, { family: 'star', index: 4 }, { family: 'star', index: 7 }];
const GLINTS_PER_SECOND = 3.5;
const GLINT_LIFE_SECONDS = 0.7;
const GLINT_SIZE_TILES = 0.24;
/** Glints show within this far of the item's center, in tiles. */
const GLINT_SPREAD_TILES = 0.28;
/** Their color: pale gold, tinged with the orb's. */
const GLINT_COLOR = Color3.FromHexString('#fff4d6');
const GLINT_ORB_SHARE = 0.3;

interface DrawnTransmutation {
    state: VeilTransmutationRenderState;
    readonly color: Color3;
    readonly twirl: Mesh;
    readonly twirlMaterial: StandardMaterial;
    orbWisps: SpriteStreamHandle | null;
    itemWisps: SpriteStreamHandle | null;
    hasFlashed: boolean;
    /** Seconds until the next glint. */
    glintIn: number;
    /** Seconds it has been drawn, to know when each glint has gone on its own. */
    ageSeconds: number;
    /** Glints still showing, with when each goes, taken away with it if it is picked up. */
    readonly glints: { readonly handle: SpriteQuadHandle; readonly goneAt: number }[];
}

/**
 * Where the item and the orb are drawn for `state`: through the swirl, the
 * orb carried and the item lifted off its rest; after it, the new item
 * settling and the orb gone.
 */
export function resolveVeilTransmutationPose(state: VeilTransmutationRenderState): VeilTransmutationPose {
    if (state.swirlProgress !== null) {
        const swirl = veilSwirlPose(state.swirlProgress, {
            orbFloorOffset: {
                x: state.orbFloorPosition.x - state.floorPosition.x,
                z: state.orbFloorPosition.z - state.floorPosition.z,
            },
            itemRestHeight: DROPPED_ITEM_HOVER_HEIGHT_TILES,
            orbRestHeight: ESSENCE_ORB_HOVER_HEIGHT_TILES,
        });
        return {
            itemOffset: swirl.itemOffset,
            itemScale: 1,
            orb: {
                center: {
                    x: state.floorPosition.x + swirl.orbCenter.x,
                    y: state.floorPosition.y + swirl.orbCenter.y,
                    z: state.floorPosition.z + swirl.orbCenter.z,
                },
                scale: swirl.orbScale,
            },
        };
    }
    const settle = veilSettlePose(state.secondsSinceDone ?? Number.POSITIVE_INFINITY);
    return { itemOffset: { x: 0, y: settle.itemOffsetY, z: 0 }, itemScale: settle.itemScale, orb: null };
}

/** Whether `state` still draws anything of its own: a swirl, or the flash, settle and glints after it. */
export function isVeilTransmutationShowing(state: VeilTransmutationRenderState): boolean {
    return state.swirlProgress !== null
        || (state.secondsSinceDone !== null && state.secondsSinceDone < Math.max(state.twinkleSeconds, FLASH_SECONDS));
}

export class VeilTransmutationSystem {
    private readonly scene: Scene;
    private readonly sprites: SpriteEffectSystem;
    private readonly drawn = new Map<string, DrawnTransmutation>();

    constructor(scene: Scene) {
        this.scene = scene;
        this.sprites = new SpriteEffectSystem(scene);
    }

    /**
     * Reconciles against the latest snapshot: starts the ones new to it,
     * updates the rest, and takes away any no longer in it (picked up, or
     * done twinkling), glints and all.
     */
    setTransmutations(states: readonly VeilTransmutationRenderState[]): void {
        const present = new Set<string>();
        for (const state of states) {
            present.add(state.id);
            const drawn = this.drawn.get(state.id);
            if (drawn) {
                drawn.state = state;
            } else {
                this.drawn.set(state.id, this.create(state));
            }
        }
        for (const [id, drawn] of this.drawn) {
            if (!present.has(id)) {
                this.remove(drawn);
                this.drawn.delete(id);
            }
        }
    }

    /** Advances the twirl, the wisps, the flash and the glints. Call every frame. */
    update(deltaSeconds: number): void {
        for (const drawn of this.drawn.values()) {
            drawn.ageSeconds += deltaSeconds;
            const { state } = drawn;
            const pose = resolveVeilTransmutationPose(state);
            if (state.swirlProgress !== null) {
                this.updateSwirl(drawn, pose, clampProgress(state.swirlProgress));
            } else {
                this.endSwirl(drawn);
                if (!drawn.hasFlashed && state.secondsSinceDone !== null) {
                    drawn.hasFlashed = true;
                    if (state.secondsSinceDone <= FLASH_LATEST_SECONDS) {
                        this.flash(drawn);
                    }
                }
                this.updateGlints(drawn, deltaSeconds);
            }
        }
        this.sprites.update(deltaSeconds);
    }

    dispose(): void {
        for (const drawn of this.drawn.values()) {
            this.remove(drawn);
        }
        this.drawn.clear();
        this.sprites.dispose();
    }

    private create(state: VeilTransmutationRenderState): DrawnTransmutation {
        const color = Color3.FromHexString(state.orbColorHexes[0] ?? '#ffffff');
        const twirlMaterial = createSpriteMaterial(this.scene, `veil-transmutation-twirl-${state.id}`, TWIRL_SPRITE, color, true);
        twirlMaterial.alpha = 0;
        const twirl = MeshBuilder.CreatePlane(`veil-transmutation-twirl-${state.id}`, { size: TWIRL_DIAMETER_TILES }, this.scene);
        twirl.material = twirlMaterial;
        twirl.isPickable = false;
        twirl.rotation.x = Math.PI / 2;
        twirl.setEnabled(false);
        return {
            state,
            color,
            twirl,
            twirlMaterial,
            orbWisps: null,
            itemWisps: null,
            // One first seen already done (no swirl seen) still flashes, if it is fresh.
            hasFlashed: false,
            glintIn: 0,
            ageSeconds: 0,
            glints: [],
        };
    }

    private updateSwirl(drawn: DrawnTransmutation, pose: VeilTransmutationPose, progress: number): void {
        const { floorPosition } = drawn.state;
        // The twirl fades in over the start and brightens as they close in.
        drawn.twirl.setEnabled(true);
        drawn.twirl.position.set(floorPosition.x, floorPosition.y + 0.012, floorPosition.z);
        drawn.twirl.rotation.y = -veilSwirlAngle(progress) * TWIRL_SPIN_SHARE;
        drawn.twirlMaterial.alpha = TWIRL_ALPHA * Math.min(1, progress / 0.2) * (0.6 + (0.4 * progress));

        const itemCenter = new Vector3(
            floorPosition.x + pose.itemOffset.x,
            floorPosition.y + DROPPED_ITEM_HOVER_HEIGHT_TILES + pose.itemOffset.y,
            floorPosition.z + pose.itemOffset.z,
        );
        drawn.itemWisps ??= this.wisps(itemCenter, ITEM_WISP_COLOR);
        drawn.itemWisps.setPosition(itemCenter);
        if (pose.orb) {
            const orbCenter = new Vector3(pose.orb.center.x, pose.orb.center.y, pose.orb.center.z);
            drawn.orbWisps ??= this.wisps(orbCenter, drawn.color);
            drawn.orbWisps.setPosition(orbCenter);
        }
    }

    /** Stops the swirl's own pieces; wisps already let go fade out on their own. */
    private endSwirl(drawn: DrawnTransmutation): void {
        drawn.twirl.setEnabled(false);
        drawn.orbWisps?.dispose();
        drawn.orbWisps = null;
        drawn.itemWisps?.dispose();
        drawn.itemWisps = null;
    }

    private wisps(position: Vector3, color: Color3): SpriteStreamHandle {
        return this.sprites.stream({
            look: WISP_LOOK,
            position,
            rate: WISP_RATE,
            emitRadius: 0.04,
            direction1: new Vector3(-0.1, 0.15, -0.1),
            direction2: new Vector3(0.1, 0.35, 0.1),
            power: [0.1, 0.3],
            lifeSeconds: [0.35, 0.6],
            size: { from: 0.16, to: 0.04 },
            color,
            alpha: 0.7,
            additive: true,
        });
    }

    /** They meet: a flash where they met, a burst of sparks, and a ring on the floor. */
    private flash(drawn: DrawnTransmutation): void {
        const { floorPosition } = drawn.state;
        const meet = new Vector3(
            floorPosition.x,
            floorPosition.y + DROPPED_ITEM_HOVER_HEIGHT_TILES + VEIL_SWIRL_LIFT_TILES,
            floorPosition.z,
        );
        const color = Color3.Lerp(drawn.color, Color3.White(), FLASH_WHITE_SHARE);
        this.sprites.quad({ sprite: FLASH_SPRITE, position: meet, facing: 'camera', color, additive: true, size: { from: 0.5, to: 1.8 }, alpha: 1, lifeSeconds: FLASH_SECONDS, fadeIn: 0.05, fadeOut: 0.8 });
        this.sprites.quad({ sprite: FLASH_STAR_SPRITE, position: meet, facing: 'camera', color: Color3.White(), additive: true, size: { from: 0.3, to: 1.1 }, alpha: 0.9, lifeSeconds: FLASH_SECONDS * 0.8, fadeIn: 0.05, fadeOut: 0.7, rotation: Math.random() * Math.PI, spin: 2.5 });
        this.sprites.quad({ sprite: FLASH_RING_SPRITE, position: new Vector3(floorPosition.x, floorPosition.y, floorPosition.z), facing: 'floor', color: drawn.color, additive: true, size: { from: 0.3, to: 1.6 }, alpha: 0.6, lifeSeconds: FLASH_SECONDS * 1.4, fadeIn: 0.05, fadeOut: 0.8 });
        this.sprites.burst({
            look: SPARK_LOOK,
            position: meet,
            count: 22,
            emitRadius: 0.05,
            direction1: new Vector3(-1, -0.3, -1),
            direction2: new Vector3(1, 1, 1),
            power: [0.8, 1.8],
            lifeSeconds: [0.25, 0.5],
            size: { from: 0.07, to: 0.02 },
            gravity: new Vector3(0, -2, 0),
            color,
            alpha: 1,
            additive: true,
        });
    }

    /** Glints wink on and off about the new item, fewer as the twinkle runs out. */
    private updateGlints(drawn: DrawnTransmutation, deltaSeconds: number): void {
        const { state } = drawn;
        // Forget the glints that have gone on their own.
        while (drawn.glints.length > 0 && drawn.glints[0]!.goneAt <= drawn.ageSeconds) {
            drawn.glints.shift();
        }
        const strength = veilTwinkleStrength(state.secondsSinceDone ?? Number.POSITIVE_INFINITY, state.twinkleSeconds);
        if (strength <= 0) {
            return;
        }
        drawn.glintIn -= deltaSeconds;
        if (drawn.glintIn > 0) {
            return;
        }
        drawn.glintIn = (1 / GLINTS_PER_SECOND) * (0.5 + Math.random()) / strength;
        const angle = Math.random() * Math.PI * 2;
        const reach = Math.sqrt(Math.random()) * GLINT_SPREAD_TILES;
        const position = new Vector3(
            state.floorPosition.x + (Math.cos(angle) * reach),
            state.floorPosition.y + DROPPED_ITEM_HOVER_HEIGHT_TILES + ((Math.random() - 0.4) * GLINT_SPREAD_TILES),
            state.floorPosition.z + (Math.sin(angle) * reach),
        );
        const sprite = GLINT_SPRITES[Math.floor(Math.random() * GLINT_SPRITES.length)] ?? GLINT_SPRITES[0]!;
        const size = GLINT_SIZE_TILES * (0.6 + (0.4 * Math.random()));
        const handle = this.sprites.quad({
            sprite,
            position,
            facing: 'camera',
            color: Color3.Lerp(GLINT_COLOR, drawn.color, GLINT_ORB_SHARE),
            additive: true,
            size: { from: size * 0.3, to: size },
            alpha: 0.95,
            lifeSeconds: GLINT_LIFE_SECONDS,
            fadeIn: 0.35,
            fadeOut: 0.6,
            rotation: Math.random() * Math.PI,
            spin: 1.5,
        });
        drawn.glints.push({ handle, goneAt: drawn.ageSeconds + GLINT_LIFE_SECONDS });
    }

    private remove(drawn: DrawnTransmutation): void {
        this.endSwirl(drawn);
        for (const glint of drawn.glints) {
            glint.handle.dispose();
        }
        drawn.glints.length = 0;
        drawn.twirlMaterial.dispose();
        drawn.twirl.dispose();
    }
}

